import React, { useMemo, useState } from 'react';
import SurveyForm from '../../SurveyForm';
import { useSurvey } from '../../admin-page/apis/useSurvey';
import { isReadOnlyPreviewEnabled } from '../../../utils/readOnlyPreview';

const PARENT_POST_SURVEY_STORAGE_KEY = 'parentPostSurveySubmitted';

const getParentPostSurveyStorageKey = () => {
  const storedName = localStorage.getItem('name')?.trim();

  return storedName
    ? `${PARENT_POST_SURVEY_STORAGE_KEY}:${storedName}`
    : PARENT_POST_SURVEY_STORAGE_KEY;
};

const getHasSubmittedPostSurvey = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return localStorage.getItem(getParentPostSurveyStorageKey()) === 'true';
};

const setHasSubmittedPostSurvey = () => {
  if (typeof window === 'undefined') {
    return;
  }

  localStorage.setItem(getParentPostSurveyStorageKey(), 'true');
};

export default function PostSurvey({ onSubmit }) {
  const [isSubmitted, setIsSubmitted] = useState(getHasSubmittedPostSurvey);
  const [submitError, setSubmitError] = useState('');
  const titleStyle = {
    width: '100%',
    maxWidth: '600px',
    margin: '0 auto 1rem',
    textAlign: 'left',
  };
  const questions = useMemo(
    () => [
      {
        name: 'concerns_change',
        type: 'single',
        label:
          "The concerns that I had about my child's behavior when I first started the ezParent program are now:",
        options: [
          { value: 'worse', label: 'Worse' },
          { value: 'same', label: 'Same' },
          { value: 'better', label: 'Better' },
          { value: 'much_better', label: 'Much better' },
        ],
      },
      {
        name: 'confidence_change',
        type: 'single',
        label:
          'Compared to when you started the ezParent program, how confident are you about managing your child’s behavior?',
        options: [
          { value: 'less_confident', label: 'Less confident than before' },
          { value: 'about_same', label: 'About the same as before' },
          { value: 'little_more', label: 'A little more confident' },
          { value: 'much_more', label: 'Much more confident' },
        ],
      },
      {
        name: 'overall_satisfaction',
        type: 'single',
        label: 'Overall, how satisfied are you with the ezParent program?',
        options: [
          { value: 'very_dissatisfied', label: 'Very dissatisfied' },
          { value: 'dissatisfied', label: 'Dissatisfied' },
          { value: 'satisfied', label: 'Satisfied' },
          { value: 'very_satisfied', label: 'Very satisfied' },
        ],
      },
      {
        name: 'skills_learned',
        type: 'multi',
        label:
          'Which parenting skills did you learn or improve by participating in the ezParent Program? (Select all that apply)',
        options: [
          { value: 'child_centered_time', label: 'Child-Centered Time' },
          { value: 'descriptive_commenting', label: 'Descriptive Commenting' },
          { value: 'routines_traditions', label: 'Routines & Traditions' },
          {
            value: 'attention_for_behavior',
            label:
              'If you want to see a behavior again, give it your attention!',
          },
          { value: 'labeled_praise', label: 'Labeled Praise' },
          { value: 'rewards', label: 'Rewards' },
          {
            value: 'say_what_you_mean',
            label: 'Say What You Mean and Mean What You Say',
          },
          { value: 'household_rules', label: 'Household Rules' },
          { value: 'do_statements', label: 'DO Statements' },
          { value: 'warning_statements', label: 'Warning Statements' },
          { value: 'if_then', label: 'If/Then and When/Then Statements' },
          {
            value: 'no_attention_for_behavior',
            label:
              'If you do NOT want to see a behavior again, do NOT give it your attention!',
          },
          { value: 'ignoring_distracting', label: 'Ignoring and Distracting' },
          {
            value: 'eight_keys',
            label: 'The 8 Keys to Effective Discipline',
          },
          { value: 'time_out', label: 'Time-Out' },
          { value: 'stress_management', label: 'Stress Management' },
          { value: 'i_statements', label: '“I” Statements' },
          { value: 'problem_solving', label: 'Problem Solving' },
        ],
      },
    ],
    [],
  );

  const parentPostSurveyMutation = useSurvey({
    url: '/api/survey/parentpost',
  });

  const handleSubmit = async (responses) => {
    setSubmitError('');

    try {
      if (!isReadOnlyPreviewEnabled()) {
        await parentPostSurveyMutation.mutateAsync(responses);
      }

      setHasSubmittedPostSurvey();
      setIsSubmitted(true);
      onSubmit?.(responses);
    } catch (error) {
      if (error?.status !== 401) {
        setSubmitError(
          'We could not save your survey right now. Please try again.',
        );
      }
    }
  };

  if (isSubmitted) {
    return (
      <div
        style={{
          marginTop: '3rem',
        }}
      >
        <h2 className='title' style={titleStyle}>
          Post Survey
        </h2>
        <div className='svy svy--submitted'>
          <section className='svy-status' aria-live='polite'>
            <h3 className='svy-status__title'>Survey submitted</h3>
            <p className='svy-status__desc'>
              Thank you for completing the ezParent post survey.
            </p>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: '3rem',
      }}
    >
      <h2 className='title' style={titleStyle}>
        Post Survey
      </h2>
      <SurveyForm
        title='Thank you for being part of the ezParent Program.'
        description='Your participation matters, and we’d appreciate your feedback to help us better support parents and families. All questions are optional — you can skip any you prefer not to answer.'
        questions={questions}
        onSubmit={handleSubmit}
        normalizeEmptyToNull={false}
        submitLabel={
          parentPostSurveyMutation.isPending ? 'Submitting...' : 'Submit'
        }
      />
      {submitError ? <p className='svy-error'>{submitError}</p> : null}
    </div>
  );
}
